import { useEffect, useState, useCallback } from "react";
import api, { money } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

const STATUS = [
  { value: "new", label: "New", cls: "bg-amber-100 text-amber-900 border-amber-300" },
  { value: "in_progress", label: "In Progress", cls: "bg-blue-100 text-blue-900 border-blue-300" },
  { value: "ready", label: "Ready for Pickup", cls: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  { value: "completed", label: "Completed", cls: "bg-stone-200 text-stone-700 border-stone-300" },
  { value: "cancelled", label: "Cancelled", cls: "bg-rose-100 text-rose-900 border-rose-300" },
];

const badgeCls = (s) => STATUS.find((x) => x.value === s)?.cls || "bg-stone-100 text-stone-700";

export default function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
    } catch (e) {
      console.error("Failed to load orders:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success("Order updated");
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const active = orders.filter((o) => !["completed", "cancelled"].includes(o.status));
  const done = orders.filter((o) => ["completed", "cancelled"].includes(o.status));

  const Card = ({ o }) => (
    <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`order-card-${o.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-display text-lg font-bold text-stone-900">{o.order_number}</div>
          <div className="text-sm text-stone-600">{o.customer?.name} · {o.customer?.phone}</div>
          <div className="mt-1 text-xs font-semibold text-orange-700">Pickup: {o.pickup_time_display}</div>
        </div>
        <span data-testid="order-status-badge" className={`rounded-full border px-3 py-1 text-xs font-bold ${badgeCls(o.status)}`}>
          {STATUS.find((s) => s.value === o.status)?.label || o.status}
        </span>
      </div>
      <div className="mt-3 space-y-1 border-t border-stone-100 pt-3 text-sm">
        {o.items.map((it, i) => (
          <div key={`${it.name}-${i}`} className="flex justify-between">
            <span className="text-stone-700">{it.quantity}× {it.name}{it.selected_options?.length ? ` (${it.selected_options.map((x) => x.label).join(", ")})` : ""}{it.special_instructions ? ` — “${it.special_instructions}”` : ""}</span>
            <span className="font-medium">{money(it.line_total)}</span>
          </div>
        ))}
      </div>
      {o.special_instructions && <div className="mt-2 text-xs italic text-amber-700">Note: {o.special_instructions}</div>}
      <div className="mt-3 flex items-center justify-between border-t border-stone-100 pt-3">
        <span className="font-bold text-stone-900">{money(o.total)}</span>
        <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
          <SelectTrigger className="w-48" data-testid={`staff-order-status-update-select-${o.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold text-stone-900">Live Orders ({active.length})</h2>
        <button onClick={load} className="flex items-center gap-2 rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-700 hover:bg-stone-100">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>
      {loading ? (
        <p className="text-stone-500">Loading…</p>
      ) : active.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-stone-300 bg-white p-8 text-center text-stone-500">No active orders right now.</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">{active.map((o) => <Card key={o.id} o={o} />)}</div>
      )}

      {done.length > 0 && (
        <>
          <h3 className="mb-3 mt-8 font-display text-lg font-bold text-stone-500">Recent completed / cancelled</h3>
          <div className="grid gap-4 lg:grid-cols-2 opacity-70">{done.slice(0, 8).map((o) => <Card key={o.id} o={o} />)}</div>
        </>
      )}
    </div>
  );
}
